import {
  DataType,
  FormDialogMode,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Button } from '@material-ui/core'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumPeriodoLiquidacion, EnumTipoCantidad } from '../../../api'
import { FlexibilitySubtypeLimits } from '../../../api/Argentia/endpoints/flexibilities/model/flexibilities'
import { EnumPeriodoCalculo } from '../../../api/oData/enums/enumPeriodoCalculo'
import { EnumSubtiposFlexibilidad } from '../../../api/oData/enums/enumSubtiposFlexibilidad'
import ButtonWithPermissions from '../../../components/buttonWithPermissions/buttonWithPermissions'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import PageLeftColumn from '../../../components/page/pageLeftColumn'
import PageRightColumn from '../../../components/page/pageRightColumn'
import { validatePriceExpression } from '../../../components/shared/priceExpressionValidator/priceExpressionValidator.helper'
import {
  enumEnumToValueNumber,
  enumToSelectOptionArray,
  generateUniqueKeyWithNoPrefix
} from '../../../helpers'
import { gridValuesGenerator } from '../../../helpers/gridValuesGenerator/gridValuesGenerator'
import { hasPermission } from '../../../helpers/permissionsHelper'
import {
  FormDialogDefinitionProps,
  useConfirmationDialog,
  useFormDialog,
  useHttpError
} from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { InformationDialogs } from '../../../model/enums/informationDialogs'
import { Resources } from '../../../model/RBAC/constants'
import { selectActiveContract } from '../../../redux/selectors/client.selectors'
import { PriceDetails } from '../../pricing/pricing.Model'
import {
  flexibilityModelToFlexibilityDetailsMapper,
  gridRowToLimitDetailsList,
  limitIsFree,
  limitIsFreeOrContractType,
  quantityFlexibilityToRequest,
  quantityInferiorLimitConsolidateMapper,
  quantitySuperiorLimitConsolidateMapper,
  subtypeQuantityTypesToSelectOptionMapper
} from '../flexibility.mapper'
import {
  FlexibilityDetails,
  Limit,
  LimitDetail,
  NewLimitDetail,
  QuantityFlexibility
} from '../flexibility.Model'
import {
  fetchContractQuantityLimits,
  fetchFlexibilityDetails,
  fetchFlexibilityGroups,
  fetchFlexibilityLimits,
  fetchFlexibilitySubtypesLimits,
  fetchFlexibilitySubtypesQuantityTypes,
  fetchInvoiceCycles,
  patchFlexibility,
  patchPrice,
  updateFlexibilityQuantity
} from '../flexibility.service'
import { FlexibilityDetail } from './components/flexibilityDetail'
import { FlexibilityPrice } from './components/flexibilityPrice'
import { FlexibilityLimitQuantity } from './components/flexibilityQuantity'
import { QuantityTable } from './components/quantityTable'

export interface FlexibilityEditPageProps {
  flexibilityId: number
  location?: {
    state: {
      settlementGroupId?: number
      settlementGroupCode?: string
    }
  }
}

const initialDetailTable = {
  year: new Date()
}

export const FlexibilityEditPage: React.FC<any> = props => {
  const history = useHistory()
  const [t] = useTranslation(['flexibility', 'flexibilityDetails', 'globalerisLibrary', 'enums'])

  const { getInformation, closeInformation } = useConfirmationDialog()
  const { info, error } = useNotify()

  const [flexibility, setFlexibility] = useState<FlexibilityDetails | undefined>(undefined)
  const [limits, setLimits] = useState<QuantityFlexibility>({} as QuantityFlexibility)
  const [limitsDB, setLimitsDB] = useState<QuantityFlexibility>({} as QuantityFlexibility)
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [expressionValidated, setExpressionValidated] = useState<boolean>(false)
  const [isPriceValid, setIsPriceValid] = useState<boolean>(false)
  const [isPriceValidating, setIsPriceValidating] = useState<boolean>(false)

  const [allowedLimits, setAllowedLimits] = useState<FlexibilitySubtypeLimits[] | undefined>(
    undefined
  )

  const [flexibilityGroupsOptions, setFlexibilityGroupsOptions] = useState<SelectOption[]>([])
  const [isFetchingGroupsOptions, setIsFetchingGroupsOptions] = useState<boolean>(false)
  const [isFetchingTypesOptions, setIsFetchingTypesOptions] = useState<boolean>(false)
  const [allowedQtyTypes, setAllowedQtyTypes] = useState<SelectOption[]>([])

  const [isFetchingCyclesOptions, setIsFetchingCyclesOptions] = useState<boolean>(false)
  const [invoiceCyclesOptions, setInvoiceCyclesOptions] = useState<SelectOption[]>([])

  const [limitIsloaded, setLimitIsLoaded] = useState<boolean>(false)
  const [allowedQtyTypesIsLoaded, setAllowedQtyTypesIsLoaded] = useState<boolean>(false)
  const [qtyIsNull, setQtyIsNull] = useState<boolean>(false)

  const contractData = useSelector(selectActiveContract)
  const settlementGroupId = props.location?.state?.settlementGroupId
  const settlementGroupCode = props.location?.state?.settlementGroupCode
  const contractID = flexibility?.contractId
  const isFlexGroup =
    flexibility?.flexibilityGroup && flexibility?.flexibilityGroup !== '' ? true : false

  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()

  const [selectedDetail, setSelectedDetail] = useState<NewLimitDetail>(
    initialDetailTable as NewLimitDetail
  )

  interface formDependenciesProps {
    formState: any
  }

  const [formDependencies, setFormDependencies] = useState<formDependenciesProps>({
    formState: {}
  })

  const { showError, clearError, errorProps } = useHttpError()

  // Form Definition Starts

  const editFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        sections: [
          {
            inputs: [
              {
                name: 'description',
                label: t('flexibilityDetails:forms.edit.inputs.description'),
                type: DataType.TEXT
              },
              {
                name: 'calculationPeriod',
                label: t('flexibilityDetails:forms.edit.inputs.calculationPeriod'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(
                  EnumPeriodoCalculo,
                  'enums:EnumPeriodoCalculo'
                ),
                required: true
              },
              {
                name: 'flexibilityGroupId',
                label: t('flexibilityDetails:forms.edit.inputs.flexibilityGroup'),
                type: DataType.SELECT,
                selectOptions: flexibilityGroupsOptions,
                isLoading: isFetchingGroupsOptions,
                clearable: true
              },
              {
                name: 'startDate',
                label: t('flexibilityDetails:forms.edit.inputs.startDate'),
                type: DataType.DATE,
                required: true
              },
              {
                name: 'endDate',
                label: t('flexibilityDetails:forms.edit.inputs.endDate'),
                type: DataType.DATE,
                required: true
              },
              {
                name: 'signDate',
                label: t('flexibilityDetails:forms.edit.inputs.signDate'),
                type: DataType.DATE,
                clearable: true,
                helpText: '* ' + t('flexibilityDetails:forms.edit.inputs.signDateHelpText')
              },
              {
                name: 'settlementPeriod',
                label: t('flexibilityDetails:forms.edit.inputs.settlementPeriod'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(
                  EnumPeriodoLiquidacion,
                  'enums:EnumPeriodoLiquidacion'
                ),
                required: true
              },
              {
                name: 'invoicingCycleId',
                label: t('flexibilityDetails:forms.edit.inputs.invoicingCycle'),
                type: DataType.SELECT,
                isLoading: isFetchingCyclesOptions,
                selectOptions: invoiceCyclesOptions,
                clearable: true
              },
              {
                name: 'comments',
                label: t('flexibilityDetails:forms.edit.inputs.comments'),
                type: DataType.TEXT,
                clearable: true
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDependencies, invoiceCyclesOptions, isFetchingCyclesOptions])

  // Form Config Ends

  // Handlers Starts

  const fetchFlexibilityGroupsOptions = async () => {
    setIsFetchingGroupsOptions(true)
    await fetchFlexibilityGroups().then(response => {
      if (response.success) {
        const options = response.data ?? []
        setFlexibilityGroupsOptions(options)
      }
      setIsFetchingGroupsOptions(false)
    })
  }

  const onEditFlexibilityDetailsClickHandler = async () => {
    const currentGroup = flexibilityGroupsOptions.find(
      option => option.label === flexibility?.flexibilityGroup
    )

    const flexData = {
      ...flexibility,
      flexibilityGroupId: currentGroup?.value
    } as FlexibilityDetails

    openForm({
      definition: editFormDefinition,
      title: t('flexibilityDetails:forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: flexData,
      onClose: onEditFlexibilityDetailsHandler
    })

    setFormDependencies({
      ...formDependencies,
      formState: flexData
    })
  }

  const onEditFlexibilityDetailsHandler = async (formData: FlexibilityDetails) => {
    if (formData) {
      // check if dates are valid
      const startDateValid = moment(formData.startDate).isSameOrBefore(formData.endDate, 'day')
      const signDateValid = formData.signDate
        ? moment(formData.signDate).isSameOrBefore(formData.startDate, 'day')
        : true
      if (!(startDateValid && signDateValid)) {
        error(t('flexibilityDetails:forms.edit.notifications.datesInvalid'))
        return
      }

      const mappedFormData: FlexibilityDetails = {
        ...formData,
        flexibilityGroup: flexibilityGroupsOptions.find(
          o => o.value === formData.flexibilityGroupId
        )?.label
      }

      await patchFlexibility(mappedFormData).then(response => {
        if (response.success) {
          fetchFlexibilityDetailsById(props.location.state.flexibilityId)
          info(t('flexibilityDetails:forms.edit.notifications.save.success'))
        } else {
          error(t('flexibilityDetails:forms.edit.notifications.save.error'))
        }
      })
    }

    closeForm()
  }

  // Handlers Ends

  // Functions Starts

  const fetchFlexibilityDetailsById = async (id: number) => {
    setIsFetching(true)

    await fetchFlexibilityDetails(id).then(response => {
      setIsFetching(false)
      if (response.success && response.data) {
        clearError()
        const flex = flexibilityModelToFlexibilityDetailsMapper(response.data)
        setFlexibility(flex)
        fetchAllowedQtyTypes(flex.subtype)
      } else {
        response.error &&
          showError({
            title: t('flexibility:title'),
            errorCode: response.error.code,
            onRefresh: async () => {
              fetchFlexibilityDetails(id)
            }
          })
      }
    })
  }

  const fetchFlexibilityLimitByID = async (id: number) => {
    setIsFetching(true)
    setLimitIsLoaded(false)
    await fetchFlexibilityLimits(id).then(response => {
      setIsFetching(false)
      if (response.success && response.data) {
        clearError()
        setLimits(response.data)
        setLimitsDB(response.data)
        setLimitIsLoaded(true)
      } else {
        response.error &&
          showError({
            title: t('flexibility:title'),
            errorCode: response.error.code,
            onRefresh: async () => {
              fetchFlexibilityLimits(id)
            }
          })
      }
    })
  }

  const handleGoBack = () => {
    if (contractData) {
      history.push(NonAuthRoutes.ContractFlexibilities)
    } else {
      history.goBack()
    }
  }

  const onSave = async () => {
    if (!flexibility?.id || !limits) {
      return
    }

    const request = quantityFlexibilityToRequest(limits, limitsDB, isFlexGroup)

    let successFlex: boolean = false
    let successPrice: boolean = true

    await updateFlexibilityQuantity(flexibility?.id, request).then(response => {
      successFlex = response.success
    })

    if (!successFlex) {
      error(t('flexibilityDetails:notifications.save.error'))
      return
    }

    if (flexibility.price.id) {
      await patchPrice(flexibility.price).then(response => {
        successPrice = response.success
      })
    }

    if (!successPrice) {
      error(t('flexibilityDetails:notifications.save.errorPrice'))
      return
    }

    info(t('flexibilityDetails:notifications.save.success'))
    if (contractData) {
      history.push(NonAuthRoutes.ContractFlexibilities)
    } else if (settlementGroupId && settlementGroupCode) {
      history.push(NonAuthRoutes.SettlementGroupFlexibilities, {
        settlementGroupId,
        settlementGroupCode
      })
    } else {
      history.goBack()
    }

    return
  }

  const handleSelect = (key: string, value: any) => {
    setFlexibility((prevState: any) => {
      const newPrice = { ...prevState?.price, [key]: value } as PriceDetails
      validatePriceExpression(newPrice, setIsPriceValid)
      return { ...prevState, price: newPrice }
    })
  }

  const handlePriceIsValid = (isValid: boolean) => {
    setExpressionValidated(isValid)
  }

  const handlePriceIsLoading = (isLoading: boolean) => {
    setIsPriceValidating(isLoading)
  }

  const fetchContractQuantities = async (
    limitType: 'inferiorLimit' | 'superiorLimit',
    quantityType: number
  ) => {
    if (!contractID) {
      setLimits(prevState => {
        let state: QuantityFlexibility = prevState
        if (limitType === 'inferiorLimit') {
          state = {
            ...state,
            inferiorLimit: null,
            inferiorQuantityType: quantityType
          }
        }
        if (limitType === 'superiorLimit') {
          state = {
            ...state,
            superiorLimit: null,
            superiorQuantityType: quantityType
          }
        }

        return state
      })
      return
    }

    await fetchContractQuantityLimits(contractID).then(response => {
      if (response.success && response.data) {
        const limitData: Limit = response.data
        setLimits(prevState => {
          let state: QuantityFlexibility = prevState
          if (limitType === 'inferiorLimit') {
            state = {
              ...state,
              inferiorLimit: { ...limitData, quantityType: prevState.inferiorQuantityType }
              // inferiorQuantityType: limitData.quantityType
            }
          }
          if (limitType === 'superiorLimit') {
            state = {
              ...state,
              superiorLimit: { ...limitData, quantityType: prevState.superiorQuantityType }
              // superiorQuantityType: limitData.quantityType
            }
          }

          return state
        })
      } else {
        error(t('flexibilityDetails:notifications.errors.fetchContractQuantities'))
        setLimits(limitsDB)
      }
    })
  }

  const handleOnChangeSuperiorLimit = (
    key: 'quantityType' | 'percentage' | 'measureUnit',
    value: any
  ) => {
    setLimits((prevState: QuantityFlexibility) => {
      let patch = prevState

      switch (key) {
        case 'quantityType': {
          if (value === EnumTipoCantidad.Libre || value === EnumTipoCantidad.Libre_sin_Fijacion) {
            openAssistantModal('superiorLimit')
          }

          if (
            value === EnumTipoCantidad.Capacidad_Contractual ||
            value === EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion
          ) {
            fetchContractQuantities('superiorLimit', value)
          }

          // BUG #51898 - when creating a new capacity, NULL or 9999 quantityType must prevent user from saving
          if (value === 9999) {
            setQtyIsNull(true)
          } else {
            setQtyIsNull(false)
          }

          patch = {
            ...prevState,
            superiorPercentage: undefined,
            superiorQuantityType: value,
            superiorLimit: {
              quantityType: value
              // details: null,
              // percentage: undefined,
              // measureUnit: undefined
            }
          }
          break
        }
        case 'percentage':
          patch = {
            ...prevState,
            superiorPercentage: value,
            superiorLimit: { ...prevState.superiorLimit, percentage: value }
          }
          break
        case 'measureUnit':
          patch = {
            ...prevState,
            superiorLimit: { ...prevState.superiorLimit, measureUnit: value }
          }
          break
      }
      return patch
    })
  }

  const handleOnChangeInferiorLimit = (
    key: 'quantityType' | 'percentage' | 'measureUnit',
    value: any
  ) => {
    setLimits((prevState: QuantityFlexibility) => {
      let patch = prevState

      switch (key) {
        case 'quantityType': {
          if (value === EnumTipoCantidad.Libre || value === EnumTipoCantidad.Libre_sin_Fijacion) {
            openAssistantModal('inferiorLimit')
          } else if (
            value === EnumTipoCantidad.Capacidad_Contractual ||
            value === EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion
          ) {
            fetchContractQuantities('inferiorLimit', value)
          }

          if (value === 9999) {
            setQtyIsNull(true)
          } else {
            setQtyIsNull(false)
          }
          patch = {
            ...prevState,
            inferiorPercentage: undefined,
            inferiorQuantityType: value,
            inferiorLimit: {
              ...prevState.inferiorLimit,
              quantityType: value
              // details: null,
              // percentage: undefined,
              // measureUnit: undefined
            }
          }
          break
        }
        case 'percentage':
          patch = {
            ...prevState,
            inferiorPercentage: value,
            inferiorLimit: { ...prevState.inferiorLimit, percentage: value }
          }
          break
        case 'measureUnit':
          patch = {
            ...prevState,
            inferiorLimit: { ...prevState.inferiorLimit, measureUnit: value }
          }
          break
      }

      return patch
    })
  }

  const showMissingLimitsConfigurationErrorMessage = async () => {
    await getInformation(
      InformationDialogs.FLEXIBILITIES_LIMITS_CONFIG_ERROR,
      t('informationDialogs:FLEXIBILITIES_LIMITS_CONFIG_ERROR.message')
    ).then(res => {
      closeInformation()
      history.goBack()
    })
  }

  const showMissingSubtypeConfigurationErrorMessage = async () => {
    await getInformation(
      InformationDialogs.FLEXIBILITIES_SUBTYPE_CONFIG_ERROR,
      t('informationDialogs:FLEXIBILITIES_SUBTYPE_CONFIG_ERROR.message')
    ).then(res => {
      closeInformation()
      history.goBack()
    })
  }

  const fetchAllowedLimits = async () => {
    await fetchFlexibilitySubtypesLimits().then(response => {
      if (response.success) {
        setAllowedLimits(response.data ?? [])
      } else {
        showMissingLimitsConfigurationErrorMessage()
      }
    })
  }

  const fetchAllowedQtyTypes = async (subtype: EnumSubtiposFlexibilidad) => {
    setIsFetchingTypesOptions(true)
    setAllowedQtyTypesIsLoaded(false)
    await fetchFlexibilitySubtypesQuantityTypes()
      .then(response => {
        if (response.success) {
          const options = response.data ?? []
          const flexSubtype = enumEnumToValueNumber(EnumSubtiposFlexibilidad, subtype)
          const filteredOptions = options.find(option => option.subtype === flexSubtype)
          if (!filteredOptions) {
            setAllowedQtyTypes([])
          } else {
            setAllowedQtyTypes(subtypeQuantityTypesToSelectOptionMapper(filteredOptions))
          }
          setAllowedQtyTypesIsLoaded(true)
        } else {
          showMissingSubtypeConfigurationErrorMessage()
        }
      })
      .finally(() => setIsFetchingTypesOptions(false))
  }

  const fetchInvoiceCyclesOptions = async () => {
    setIsFetchingCyclesOptions(true)
    await fetchInvoiceCycles()
      .then(response => {
        if (response.success) {
          const optionsList = response.data
          setInvoiceCyclesOptions(optionsList ?? [])
        }
      })
      .finally(() => {
        setIsFetchingCyclesOptions(false)
      })
  }

  const handleAddRow = (detail: LimitDetail, limit: 'inferiorLimit' | 'superiorLimit') => {
    const newDetail = {
      ...detail,
      tempId: generateUniqueKeyWithNoPrefix()
    }

    setLimits(quantityFlexibility => {
      return {
        ...quantityFlexibility,
        [limit]: {
          ...quantityFlexibility[limit],
          details: [...quantityFlexibility[limit]!.details!, newDetail]
        }
      }
    })
  }

  const handleEditRow = (detail: LimitDetail, limit: 'inferiorLimit' | 'superiorLimit') => {
    const limitsList =
      limit === 'inferiorLimit' ? limits?.inferiorLimit?.details : limits?.superiorLimit?.details
    const detailItem = limitsList?.find(
      c =>
        (c.idDetail && c.idDetail === detail.idDetail) ||
        (!c.idDetail && c.tempId === detail.tempId)
    )

    const updatedDetail = {
      ...detailItem,
      ...detail,
      tempId: `${detailItem?.tempId ?? detailItem?.idDetail}`
    }

    setLimits(quantityFlexibility => {
      return {
        ...quantityFlexibility,
        [limit]: {
          ...quantityFlexibility[limit],
          details: quantityFlexibility[limit]!.details!.map(d => {
            if (
              (d.idDetail && d.idDetail === updatedDetail.idDetail) ||
              (!d.idDetail && d.tempId === updatedDetail.tempId)
            ) {
              return updatedDetail
            }
            return d
          })
        }
      }
    })
  }

  const handleDeleteRow = (id: number | string, limit: 'inferiorLimit' | 'superiorLimit') => {
    setLimits(quantityFlexibility => {
      return {
        ...quantityFlexibility,
        [limit]: {
          ...quantityFlexibility[limit],
          details: quantityFlexibility[limit]!.details!.filter(
            detail =>
              (detail.idDetail && detail.idDetail !== id) ||
              (!detail.idDetail && detail.tempId !== id)
          )
        }
      }
    })
  }

  const openAssistantModal = (limitType: 'inferiorLimit' | 'superiorLimit', append?: boolean) => {
    openForm({
      definition: detailAssistantDefinition,
      title: t('flexibilityDetails:detailAssistant.title'),
      okBtnLabel: t('flexibilityDetails:detailAssistant.actions.okButton'),
      cancelBtnLabel: t('flexibilityDetails:detailAssistant.actions.cancelButton'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: formData => {
        formData
          ? handleGenerateValues(formData, limitType, append)
          : handleCloseAssistantModal(limitType)
      }
    })
  }

  const handleGenerateValues = (
    formData,
    limitType: 'inferiorLimit' | 'superiorLimit',
    append?: boolean
  ) => {
    const startDate = moment(formData.year).startOf('year').toDate()
    const endDate = moment(formData.year).endOf('year').toDate()
    const generatedValues = gridValuesGenerator({
      startDate,
      endDate,
      period: formData.idSettlementPeriod,
      fixedQuantity: 0
    })
    const limitDetails = gridRowToLimitDetailsList(generatedValues.value!)
    generatedValues?.value &&
      setLimits(prevState => ({
        ...prevState,
        [limitType]: prevState[limitType]
          ? {
              ...prevState[limitType],
              details: append
                ? [...prevState[limitType]!.details!, ...limitDetails]
                : (limitDetails as LimitDetail[])
            }
          : { details: limitDetails as LimitDetail[] }
      }))
    closeForm()
  }

  const handleCloseAssistantModal = (limitType: 'inferiorLimit' | 'superiorLimit') => {
    setLimits(prevState => {
      return {
        ...prevState,
        [limitType]: {
          ...prevState[limitType],
          details: []
        }
      }
    })
    closeForm()
  }

  const detailAssistantDefinition: FormDialogDefinitionProps =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'DETAIL_ASSISTANT',
        sections: [
          {
            inputs: [
              {
                name: 'subtitleHelpText',
                labelTitle: t('flexibilityDetails:detailAssistant.inputs.subtitleHelpText'),
                type: DataType.LABEL
              },
              {
                name: 'idSettlementPeriod',
                label: t('flexibilityDetails:detailAssistant.inputs.settlementPeriod'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(
                  EnumPeriodoLiquidacion,
                  'enums:EnumPeriodoLiquidacion'
                ).filter(
                  option =>
                    option.value !== EnumPeriodoLiquidacion.Horario &&
                    option.value !== EnumPeriodoLiquidacion.Diario &&
                    option.value !== EnumPeriodoLiquidacion.Indefinido
                ),
                onChange(value, formState) {
                  setSelectedDetail(prevState => ({
                    ...prevState,
                    idSettlementPeriod: +value
                  }))
                }
              },
              {
                name: 'year',
                label: t('flexibilityDetails:detailAssistant.inputs.year'),
                type: DataType.DATE,
                views: ['year'],
                format: 'YYYY',
                defaultValue: moment(new Date()),
                onChange(value, formState) {
                  if (moment(value).isValid()) {
                    const startDate = moment(value).startOf('year').toDate()
                    const endDate = moment(value).endOf('year').toDate()
                    setSelectedDetail(prevState => ({
                      ...prevState,
                      year: moment(value).toDate(),
                      startDate,
                      endDate
                    }))
                  }
                }
              }
            ]
          }
        ]
      }
    }, [])

  const validateMeasureUnit = (limit: Limit | null) => {
    // measureUnit is optional
    // if (limitIsFreeOrContractType(limit)) {
    //   return !!limit?.measureUnit
    // }
    return true
  }

  const validatePercentage = (limit: Limit | null) => {
    if (limit && !limitIsFree(limit)) {
      // BUG #53648 - when limit quantityType is Free, percentage is NOT required
      return limit.percentage ? true : false
    }
    return true
  }

  const validateContractType = (limit: Limit | null) => {
    if (limit) {
      return limit.quantityType === 9999 ? false : true // Bug #51898 type Nulo must not allowed to Edit Flexibility once created
    }
    return true
  }
  // Functions Ends

  // Hooks Starts

  useEffect(() => {
    fetchFlexibilityGroupsOptions()
    fetchInvoiceCyclesOptions()
    fetchAllowedLimits()
    fetchFlexibilityDetailsById(props.location.state.flexibilityId)
    fetchFlexibilityLimitByID(props.location.state.flexibilityId)
  }, [])

  useEffect(() => {
    updateFormDefinition(detailAssistantDefinition, selectedDetail)
  }, [detailAssistantDefinition])

  const allowedLimitsForSubtype =
    flexibility?.subtype &&
    allowedLimits?.find(
      x => x.subtype.id === enumEnumToValueNumber(EnumSubtiposFlexibilidad, flexibility?.subtype)
    )

  // BUG #51898 - when needed data is loaded, Nulo label must be added to quantityTypeListOption State
  useEffect(() => {
    if (
      allowedQtyTypesIsLoaded &&
      limitIsloaded &&
      allowedLimitsForSubtype &&
      allowedLimitsForSubtype.allowedLimits >= 2 &&
      !limits.idQuantityFlex
    ) {
      setAllowedQtyTypes(prevState => [...prevState, { value: 9999, label: 'Nulo' }])
      setQtyIsNull(true)
    }
  }, [allowedQtyTypesIsLoaded, limitIsloaded, allowedLimitsForSubtype])

  // Hooks Ends

  return (
    <Page layout={PageLayout.MULTI_COLUMN} error={errorProps}>
      <PageLeftColumn>
        <FlexibilityDetail flexibility={flexibility} />
      </PageLeftColumn>
      <PageCenterColumn>
        <PageHeader title={t('flexibilityDetails:title')}>
          <Button onClick={handleGoBack} size="small" color="primary">
            {t('flexibilityDetails:mainActions.back')}
          </Button>
          <ButtonWithPermissions
            onClick={onEditFlexibilityDetailsClickHandler}
            size="small"
            color="primary"
            hasPermission={hasPermission(Resources.Flexibility, [UserPermission.EDIT])}
          >
            {t('flexibilityDetails:mainActions.editFlexibility')}
          </ButtonWithPermissions>
          <ButtonWithPermissions
            variant="contained"
            disabled={
              !isPriceValid ||
              !expressionValidated ||
              isPriceValidating ||
              !validateMeasureUnit(limits.inferiorLimit) ||
              !validateMeasureUnit(limits.superiorLimit) ||
              !validatePercentage(limits.inferiorLimit) ||
              !validatePercentage(limits.superiorLimit) ||
              !validateContractType(limits.inferiorLimit) ||
              !validateContractType(limits.superiorLimit) ||
              qtyIsNull
            }
            onClick={onSave}
            size="small"
            color="primary"
            hasPermission={hasPermission(Resources.Flexibility, [UserPermission.EDIT])}
          >
            {t('flexibilityDetails:mainActions.saveFlexibility')}
          </ButtonWithPermissions>
        </PageHeader>
        <FlexibilityPrice
          price={flexibility?.price}
          onChangeInput={handleSelect}
          onIsValid={handlePriceIsValid}
          onIsLoading={handlePriceIsLoading}
        />

        {allowedLimitsForSubtype?.allowedLimits === 2 ||
        allowedLimitsForSubtype?.allowedLimits === 4 ? (
          <FlexibilityLimitQuantity
            key="inferiorLimit"
            subtitle={'Límite Inferior'}
            quantityTypeOptions={allowedQtyTypes}
            isFetchingTypesOptions={isFetchingTypesOptions}
            qtyIsNull={qtyIsNull}
            limit={quantityInferiorLimitConsolidateMapper(limits)}
            onChangeInput={(key, value) => handleOnChangeInferiorLimit(key, value)}
            isFlexGroup={isFlexGroup}
          >
            <QuantityTable
              key="inferiorLimitTable"
              limitDetails={limits?.inferiorLimit?.details}
              canEdit={
                enumEnumToValueNumber(EnumTipoCantidad, limits?.inferiorQuantityType) !==
                  EnumTipoCantidad.Capacidad_Contractual &&
                enumEnumToValueNumber(EnumTipoCantidad, limits?.inferiorQuantityType) !==
                  EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion
              }
              onAddMassiveClickHandler={() => openAssistantModal('inferiorLimit', true)}
              onAddRowValue={row => handleAddRow(row, 'inferiorLimit')}
              onEditRowValues={row => handleEditRow(row, 'inferiorLimit')}
              onDeleteRow={row => handleDeleteRow(row, 'inferiorLimit')}
            />
          </FlexibilityLimitQuantity>
        ) : null}

        {allowedLimitsForSubtype?.allowedLimits && allowedLimitsForSubtype.allowedLimits > 2 ? (
          <FlexibilityLimitQuantity
            key="superiorLimit"
            subtitle={'Límite Superior'}
            quantityTypeOptions={allowedQtyTypes}
            isFetchingTypesOptions={isFetchingTypesOptions}
            limit={quantitySuperiorLimitConsolidateMapper(limits)}
            qtyIsNull={qtyIsNull}
            onChangeInput={(key, value) => handleOnChangeSuperiorLimit(key, value)}
            isFlexGroup={isFlexGroup}
          >
            <QuantityTable
              key="superiorLimitTable"
              limitDetails={limits?.superiorLimit?.details}
              canEdit={
                enumEnumToValueNumber(EnumTipoCantidad, limits?.inferiorQuantityType) !==
                  EnumTipoCantidad.Capacidad_Contractual &&
                enumEnumToValueNumber(EnumTipoCantidad, limits?.inferiorQuantityType) !==
                  EnumTipoCantidad.Capacidad_Contractual_sin_Fijacion
              }
              onAddMassiveClickHandler={() => openAssistantModal('superiorLimit', true)}
              onAddRowValue={row => handleAddRow(row, 'superiorLimit')}
              onEditRowValues={row => handleEditRow(row, 'superiorLimit')}
              onDeleteRow={row => handleDeleteRow(row, 'superiorLimit')}
            />
          </FlexibilityLimitQuantity>
        ) : null}
        <CustomFormDialog {...formProps}></CustomFormDialog>
      </PageCenterColumn>
      <PageRightColumn></PageRightColumn>
    </Page>
  )
}
